import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Form, InputGroup, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';

const HasilLab = () => {
  const navigate = useNavigate();

  // ── State Jam & Tanggal Realtime ──
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── State Pencarian Nota ──
  const [noNotaInput, setNoNotaInput] = useState('');
  const [isLoadingPasien, setIsLoadingPasien] = useState(false);
  const [isLoadingHasil, setIsLoadingHasil] = useState(false);
  
  // ── State Data ──
  const [pasienData, setPasienData] = useState(null);
  const [hasilLabList, setHasilLabList] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // ── State Error Modal ──
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // ── State Preview Modal ──
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  // ── State Cetak Gabungan ──
  const [showKuitansiModal, setShowKuitansiModal] = useState(false);
  const [noKuitansiInput, setNoKuitansiInput] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Clock Effect
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSearchNota = async (e) => {
    if (e) e.preventDefault();
    if (!noNotaInput.trim()) return;

    setErrorMessage('');
    setPasienData(null);
    setHasilLabList([]);
    setIsLoadingPasien(true);
    setIsLoadingHasil(true);

    const apiUrl = getApiUrl();
    const nota = noNotaInput.trim();

    try {
      // 1. Fetch Data Pasien berdasarkan Nota
      let resPasien;
      try {
        resPasien = await fetch(`${apiUrl}/ceknotalab`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonota: nota })
        });
      } catch (err) {
        // Fallback GET
        resPasien = await fetch(`${apiUrl}/ceknotalab?nonota=${nota}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (resPasien.ok) {
        const jsonPasien = await resPasien.json();
        if (jsonPasien && (jsonPasien.nmpasien || jsonPasien.status === 1 || jsonPasien.msg === "Data nota laboratorium berhasil ditemukan")) {
          setPasienData(jsonPasien);
        } else {
          setErrorMessage(jsonPasien?.msg || 'Data nota laboratorium tidak ditemukan.');
          setIsLoadingPasien(false);
          setIsLoadingHasil(false);
          return;
        }
      } else {
        throw new Error(`Gagal memuat data pasien (HTTP ${resPasien.status})`);
      }
      setIsLoadingPasien(false);

      // 2. Fetch Hasil Lab berdasarkan Nota
      let resHasil;
      try {
        resHasil = await fetch(`${apiUrl}/show_hasillab`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonota: nota })
        });
      } catch (err) {
        // Fallback GET
        resHasil = await fetch(`${apiUrl}/show_hasillab?nonota=${nota}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (resHasil.ok) {
        const jsonHasil = await resHasil.json();
        const hasilList = jsonHasil?.isi_hasil || jsonHasil?.data || [];
        setHasilLabList(hasilList);
      } else {
        throw new Error(`Gagal memuat hasil lab (HTTP ${resHasil.status})`);
      }

    } catch (err) {
      console.error(err);
      const errMsg = err.message || 'Terjadi kesalahan saat menghubungi server.';
      setErrorMessage(errMsg);
      setErrorModalMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setIsLoadingPasien(false);
      setIsLoadingHasil(false);
    }
  };

  const handleHasilChange = (idx, field, value) => {
    const updatedList = [...hasilLabList];
    updatedList[idx][field] = value;
    setHasilLabList(updatedList);
  };

  const handleSimpanHasil = () => {
    // TODO: Implementasi endpoint penyimpanan ke backend
    alert('Fungsi simpan belum tersambung ke endpoint backend. (Tunggu konfirmasi endpoint dari pengguna).');
    console.log('Payload yang akan disimpan:', {
      nonota: noNotaInput,
      hasil_lab: hasilLabList
    });
  };

  const handleRefresh = () => {
    setNoNotaInput('');
    setPasienData(null);
    setHasilLabList([]);
    setErrorMessage('');
  };

  const handleCetakHasil = () => {
    setPreviewUrl(`${getApiUrl()}/cetakhasillab?nonota=${noNotaInput}`);
    setShowPreviewModal(true);
  };

  const handleCetakGabungan = async (e) => {
    if (e) e.preventDefault();
    if (!noKuitansiInput.trim()) return;
    
    setIsGeneratingPdf(true);
    setErrorMessage('');

    try {
      const res = await fetch(`${getApiUrl()}/cetakhasillab_all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf'
        },
        body: JSON.stringify({ nokuitansi: noKuitansiInput.trim() })
      });

      if (!res.ok) {
        throw new Error(`Gagal memuat dokumen dari server (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      setPreviewUrl(objectUrl);
      setShowKuitansiModal(false);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      setErrorModalMessage(err.message || 'Terjadi kesalahan saat memproses PDF.');
      setShowErrorModal(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div style={styles.appContainer}>
      {/* ── NAVBAR HEADER ── */}
      <div style={styles.navbarCustom} className="shadow-sm d-flex justify-content-between align-items-center d-print-none">
        <div className="d-flex align-items-center gap-3">
          <Button variant="link" onClick={() => navigate(-1)} className="p-0 text-white" style={{ textDecoration: 'none' }}>
            <i className="bi bi-arrow-left fs-5"></i>
          </Button>
          <div>
            <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2" style={{ letterSpacing: '0.5px' }}>
              <i className="bi bi-clipboard2-pulse"></i>
              Input Hasil Laboratorium
            </h5>
            <small className="text-white-50" style={{ fontSize: '0.75rem' }}>SIMRS V2 - Modul Laboratorium</small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="text-end d-none d-md-block text-white-50" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
            <div className="text-white fw-bold">{time.clock}</div>
            <div>{time.date}</div>
          </div>
          <Button
            variant="warning"
            size="sm"
            className="fw-semibold text-dark shadow-sm"
            style={{ padding: '0.3rem 0.8rem', fontSize: '0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill me-1"></i> Kembali ke Home
          </Button>
        </div>
      </div>

      <div style={styles.mainContent}>
        <Row className="g-3">
          {/* ── PANEL KIRI: PENCARIAN & INFO PASIEN ── */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', minHeight: '100%' }}>
              <Card.Header className="bg-white border-bottom-0 pt-3 pb-2 px-4 d-flex justify-content-between align-items-center d-print-none">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-search text-teal" style={{ color: '#0d9488' }}></i> Cari Nota Laboratorium
                </h6>
                <Button 
                  variant="light" 
                  size="sm" 
                  onClick={handleRefresh} 
                  title="Bersihkan form dan mulai dari awal" 
                  className="fw-bold d-flex align-items-center gap-2 shadow-sm" 
                  style={{ 
                    fontSize: '0.75rem', 
                    borderRadius: '20px', 
                    color: '#0f766e', 
                    backgroundColor: '#f0fdfa', 
                    border: '1px solid #ccfbf1', 
                    padding: '0.35rem 0.85rem' 
                  }}
                >
                  <i className="bi bi-arrow-clockwise" style={{ fontSize: '0.85rem' }}></i> Segarkan
                </Button>
              </Card.Header>
              <Card.Body className="px-4">
                <div className="d-print-none">
                <Form onSubmit={handleSearchNota}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Nomor Nota</Form.Label>
                    <InputGroup>
                      <Form.Control
                        placeholder="Ketik No Nota..."
                        value={noNotaInput}
                        onChange={(e) => setNoNotaInput(e.target.value)}
                        style={{ fontSize: '0.85rem' }}
                      />
                      <Button type="submit" variant="teal" disabled={isLoadingPasien} style={{ background: '#0d9488', borderColor: '#0d9488', color: 'white' }}>
                        {isLoadingPasien ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search"></i>}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Form>

                {errorMessage && (
                  <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.75rem' }}>
                    <i className="bi bi-exclamation-circle-fill me-2"></i>{errorMessage}
                  </div>
                )}
                </div>

                {pasienData && (
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '0.85rem' }}>Informasi Pasien</h6>
                    <Row className="g-3" style={{ fontSize: '0.8rem' }}>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Nama Pasien</div>
                        <div className="fw-bold text-dark">{pasienData.nmpasien}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>No. RM / Kelas</div>
                        <div className="fw-semibold text-dark">{pasienData.kelas}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Umur / Kelamin</div>
                        <div className="fw-semibold text-dark">{pasienData.umur} • {pasienData.kelamin === 'L' ? 'L' : pasienData.kelamin === 'P' ? 'P' : pasienData.kelamin}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Jaminan</div>
                        <div className="fw-semibold text-dark">{pasienData.nmklasifikasi}</div>
                      </Col>
                      <Col xs={12} className="mt-3 pt-3 border-top">
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Dokter Pengirim</div>
                        <div className="fw-bold text-teal" style={{ color: '#0d9488' }}>{pasienData.nmdokter}</div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Tgl: {pasienData.tanggal}</div>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* ── PANEL KANAN: TABEL HASIL LAB ── */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', minHeight: '100%' }}>
              <Card.Header className="bg-white border-bottom pb-2 pt-3 px-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-list-check text-teal" style={{ color: '#0d9488' }}></i> Daftar Hasil Pemeriksaan
                </h6>
                <div className="d-flex gap-2 d-print-none">
                  <Button variant="outline-primary" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={() => setShowKuitansiModal(true)}>
                    <i className="bi bi-collection"></i> Cetak Gabungan
                  </Button>
                  {hasilLabList.length > 0 && (
                    <>
                      <Button variant="outline-secondary" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={handleCetakHasil}>
                        <i className="bi bi-printer-fill"></i> Cetak Nota
                      </Button>
                      <Button variant="success" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={handleSimpanHasil}>
                        <i className="bi bi-save-fill"></i> Simpan Hasil
                      </Button>
                    </>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                {!pasienData && !isLoadingHasil ? (
                  <div className="text-center text-muted py-5 mt-4">
                    <i className="bi bi-inbox fs-1 text-secondary mb-3 d-block"></i>
                    Silakan cari Nomor Nota terlebih dahulu untuk melihat form input hasil laboratorium.
                  </div>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                    <Table responsive hover size="sm" className="align-middle mb-0 border-0" style={{ fontSize: '0.78rem' }}>
                      <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th className="text-center text-muted fw-semibold py-2" style={{ width: '40px' }}>#</th>
                          <th className="text-muted fw-semibold py-2" style={{ minWidth: '150px' }}>Pemeriksaan</th>
                          <th className="text-center text-muted fw-semibold py-2" style={{ width: '130px' }}>Hasil 1</th>
                          <th className="text-center text-muted fw-semibold py-2" style={{ width: '130px' }}>Hasil 2</th>
                          <th className="text-center text-muted fw-semibold py-2" style={{ width: '100px' }}>Satuan</th>
                          <th className="text-center text-muted fw-semibold py-2" style={{ minWidth: '120px' }}>Nilai Normal</th>
                          <th className="text-center text-muted fw-semibold py-2" style={{ width: '110px' }}>Kesimpulan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingHasil ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Spinner animation="border" variant="teal" size="sm" className="me-2" style={{ color: '#0d9488' }} />
                              <span className="text-muted">Mengambil data pemeriksaan...</span>
                            </td>
                          </tr>
                        ) : hasilLabList.length > 0 ? (
                          hasilLabList.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td className="text-center text-muted">{idx + 1}</td>
                              <td>
                                <div className="fw-bold text-dark">{item.nm_tindakan}</div>
                                <div className="text-muted font-monospace" style={{ fontSize: '0.65rem' }}>{item.kode_tindakan} | {item.nm_jenis}</div>
                              </td>
                              <td className="text-center p-2">
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  className="text-center fw-bold text-primary shadow-none"
                                  style={{ fontSize: '0.8rem', border: '1px solid transparent', borderBottom: '1px solid #cbd5e1', borderRadius: '0', backgroundColor: 'transparent' }}
                                  value={item.hasil || ''}
                                  onChange={(e) => handleHasilChange(idx, 'hasil', e.target.value)}
                                  placeholder="-"
                                />
                              </td>
                              <td className="text-center p-2">
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  className="text-center fw-semibold text-secondary shadow-none"
                                  style={{ fontSize: '0.8rem', border: '1px solid transparent', borderBottom: '1px solid #cbd5e1', borderRadius: '0', backgroundColor: 'transparent' }}
                                  value={item.hasil2 || ''}
                                  onChange={(e) => handleHasilChange(idx, 'hasil2', e.target.value)}
                                  placeholder="-"
                                />
                              </td>
                              <td className="text-center fw-semibold text-dark">{item.satuan || '-'}</td>
                              <td className="text-center font-monospace text-muted" style={{ fontSize: '0.72rem' }}>
                                {item.nilainormal1 && item.nilainormal2 
                                  ? `${item.nilainormal1} - ${item.nilainormal2}`
                                  : item.nilainormal1 || item.nilainormal2 || '-'}
                              </td>
                              <td className="text-center p-2">
                                <Form.Select
                                  size="sm"
                                  className="text-center fw-semibold shadow-none"
                                  style={{ 
                                    fontSize: '0.75rem', 
                                    border: '1px solid transparent', 
                                    borderBottom: '1px solid #cbd5e1',
                                    borderRadius: '0',
                                    backgroundColor: 'transparent',
                                    color: item.kesimpulan?.toLowerCase() === 'rendah' ? '#dc3545' 
                                         : item.kesimpulan?.toLowerCase() === 'tinggi' ? '#dc3545' 
                                         : item.kesimpulan?.toLowerCase() === 'normal' ? '#198754' 
                                         : '#475569'
                                  }}
                                  value={item.kesimpulan || ''}
                                  onChange={(e) => handleHasilChange(idx, 'kesimpulan', e.target.value)}
                                >
                                  <option value="">--</option>
                                  <option value="Normal">Normal</option>
                                  <option value="Rendah">Rendah</option>
                                  <option value="Tinggi">Tinggi</option>
                                  <option value="Positif">Positif</option>
                                  <option value="Negatif">Negatif</option>
                                  <option value="Reaktif">Reaktif</option>
                                  <option value="Non Reaktif">Non Reaktif</option>
                                </Form.Select>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4 text-muted">
                              Tidak ada data item pemeriksaan untuk nota ini.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ── Modal Error Notifikasi ── */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered backdrop="static" size="sm">
        <Modal.Body className="text-center p-4">
          <div className="mb-3">
            <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '3rem' }}></i>
          </div>
          <h6 className="fw-bold text-dark mb-2">Peringatan</h6>
          <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            {errorModalMessage}
          </p>
          <Button variant="danger" size="sm" className="px-4 fw-bold rounded-pill" onClick={() => setShowErrorModal(false)}>
            Mengerti
          </Button>
        </Modal.Body>
      </Modal>

      {/* ── Modal Preview Cetak ── */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '1.1rem' }}>
            <i className="bi bi-printer text-teal" style={{ color: '#0d9488' }}></i>
            Preview Cetak Hasil Laboratorium
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '75vh', backgroundColor: '#e2e8f0' }}>
          <iframe 
            id="previewIframe"
            src={previewUrl} 
            width="100%" 
            height="100%" 
            style={{ border: 'none' }}
            title="Preview Cetak"
          />
        </Modal.Body>
        <Modal.Footer className="bg-light border-top">
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Tutup
          </Button>
          <a href={previewUrl} download={`HasilLab_${noKuitansiInput || noNotaInput}.pdf`} className="btn btn-outline-primary d-flex align-items-center gap-1">
            <i className="bi bi-download"></i> Download PDF
          </a>
          <Button 
            variant="teal" 
            style={{ background: '#0d9488', borderColor: '#0d9488', color: 'white' }} 
            onClick={() => {
              const iframe = document.getElementById('previewIframe');
              if(iframe) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }}
          >
            <i className="bi bi-printer-fill me-1"></i> Cetak Dokumen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Input Kuitansi untuk Cetak Gabungan ── */}
      <Modal show={showKuitansiModal} onHide={() => setShowKuitansiModal(false)} centered backdrop="static" size="sm">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '1rem' }}>
            <i className="bi bi-input-cursor-text text-primary"></i> Input Kuitansi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleCetakGabungan}>
            <Form.Group className="mb-3">
              <Form.Label className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Nomor Kuitansi</Form.Label>
              <Form.Control
                autoFocus
                placeholder="Masukkan Nomor Kuitansi..."
                value={noKuitansiInput}
                onChange={(e) => setNoKuitansiInput(e.target.value)}
                style={{ fontSize: '0.85rem' }}
              />
            </Form.Group>
            <Button type="submit" variant="primary" size="sm" className="w-100 fw-bold rounded-pill" disabled={!noKuitansiInput.trim() || isGeneratingPdf}>
              {isGeneratingPdf ? <><Spinner size="sm" animation="border" className="me-2"/> Memproses...</> : <><i className="bi bi-search me-1"></i> Preview Cetak Gabungan</>}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

const styles = {
  appContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.45rem 1rem',
    borderBottom: '2px solid #0d9488'
  },
  mainContent: {
    padding: '0.65rem 0.85rem'
  }
};

export default HasilLab;
